import { OrderEventTypes, OrderStatus } from '@hero/schema';

export type TimelineNameId =
  | 'order_created'
  | 'order_booked_by_carrier'
  | 'start_journey'
  | 'customer_confirmation'
  | 'in_transit_to_pickup'
  | 'items_picked_up'
  | 'in_transit_to_dropoff'
  | 'items_dropped_off'
  | 'completion'
  | 'cancelation';

type TimelineItem = Readonly<{
  /** the id of the step. */
  id: TimelineNameId;
  /** the events in the timeline that will be used to indicate that the item has occured or not.
   *
   * **THE ORDER OF THE EVENTS IS IMPORTANT** because it will be used to date of the first or last occurrence.
   */
  occurrenceIndicators: OrderEventTypes[];
  /** when an event occured multiple time, then this tells which date to use.
   *
   * by default, it will use the first event date.
   */
  useLastOccurrenceDate?: boolean;
  /** the indicators that will be used to determine if the step is active or not.
   *
   * similar to `occurrenceIndicators`, but this will tell how you know if the step is work in prgoress not already occured.
   */
  inProgressIndicators?: {
    /** determine if the step is in progress based on the order status. */
    status?: OrderStatus['order'][];
  };

  /** the target type of the destination. This will mainly beused to determine the in_transit to pickup or dropoff.
   *
   * we will be using the destination id in `order.destinations` array .
   */
  destinationTargetType?: 'dropoff' | 'pickup';
  /** this just hides the step if the step is not in progress */
  hideIfNotInProgress?: boolean;
  /** hides the step entirely unless it has actually occurred */
  hideIfNotOccurred?: boolean;
  /** Translation key for the completed/default label (namespace-relative, e.g. "timeline.load_booked") */
  labelKey: string;
  /** Translation key for the in-progress/active label */
  activeLabelKey?: string;
}>;

const steps: Readonly<Record<TimelineNameId, TimelineItem>> = {
  order_created: {
    id: 'order_created',
    occurrenceIndicators: ['order_created'],
    labelKey: 'timeline.load_booked',
  },

  customer_confirmation: {
    id: 'customer_confirmation',
    occurrenceIndicators: ['order_is_confirmed'],
    inProgressIndicators: {
      status: ['new', 'accepted'],
    },
    labelKey: 'timeline.order_confirmed',
    activeLabelKey: 'timeline.awaiting_confirmation',
  },

  start_journey: {
    id: 'start_journey',
    occurrenceIndicators: ['journey_start'],
    inProgressIndicators: {
      status: ['confirmed'],
    },
    hideIfNotInProgress: true,
    labelKey: 'timeline.journey_started',
    activeLabelKey: 'timeline.ready_to_start',
  },

  // * pickup
  in_transit_to_pickup: {
    id: 'in_transit_to_pickup',
    occurrenceIndicators: ['arrived'],
    destinationTargetType: 'pickup',
    inProgressIndicators: {
      status: ['in_transit_pickup'],
    },
    labelKey: 'timeline.arrived_at_pickup',
    activeLabelKey: 'timeline.heading_to_pickup',
  },

  items_picked_up: {
    id: 'items_picked_up',
    occurrenceIndicators: ['items_picked_up'],
    inProgressIndicators: {
      status: ['arrived_pickup'],
    },
    labelKey: 'timeline.picked_up',
    activeLabelKey: 'timeline.loading_items',
  },

  // * dropoff
  in_transit_to_dropoff: {
    id: 'in_transit_to_dropoff',
    destinationTargetType: 'dropoff',
    occurrenceIndicators: ['arrived'],
    inProgressIndicators: {
      status: ['in_transit_dropoff'],
    },
    labelKey: 'timeline.arrived_at_dropoff',
    activeLabelKey: 'timeline.heading_to_dropoff',
  },

  items_dropped_off: {
    id: 'items_dropped_off',
    occurrenceIndicators: ['items_dropped_off'],
    inProgressIndicators: {
      status: ['arrived_dropoff'],
    },
    labelKey: 'timeline.dropoff',
    activeLabelKey: 'timeline.unloading_items',
  },

  // * done
  completion: {
    id: 'completion',
    occurrenceIndicators: ['delivered'],
    labelKey: 'timeline.completed',
  },

  cancelation: {
    id: 'cancelation',
    occurrenceIndicators: ['customer_canceled_order', 'super_admin_canceled_order'],
    labelKey: 'timeline.canceled',
    hideIfNotOccurred: true,
  },

  order_booked_by_carrier: {
    id: 'order_booked_by_carrier',
    occurrenceIndicators: ['order_booked', 'carrier_assigned', 'order_is_confirmed'],
    labelKey: 'timeline.carrier_assigned',
  },
} as const;

const customersTimeline: Readonly<TimelineItem[]> = [
  steps.order_created,
  steps.customer_confirmation,
  steps.start_journey,
  steps.in_transit_to_pickup,
  steps.items_picked_up,
  steps.in_transit_to_dropoff,
  steps.items_dropped_off,
  steps.completion,
  steps.cancelation,
];

const carriersTimeline: Readonly<TimelineItem[]> = [
  { ...steps.start_journey, hideIfNotInProgress: false },
  steps.in_transit_to_pickup,
  steps.items_picked_up,
  steps.in_transit_to_dropoff,
  steps.items_dropped_off,
  steps.completion,
  steps.cancelation,
];

export const orderTimelineConfig = {
  /** all timeline steps */
  steps,

  /** the list of steps for the customer timeline */
  customersTimeline,

  /** the list of steps for the carrier timeline */
  carriersTimeline,
} as const;
